import React from 'react'

import { Stack } from '@mui/material'

import { InfoTooltip } from 'components/feedback/InfoTooltip'

/**
 * A DataGrid column header that carries its term's definition.
 *
 * ## Why a shared helper rather than a `renderHeader` per column
 *
 * There were already two ways to put an explanation on a header and neither was reusable:
 * a local `headerWithInfo` closure inside `TagInventoryPage` built on a raw MUI `Tooltip`,
 * and nothing at all everywhere else. The local one is the reason this exists — it is a
 * third hand-roll waiting to happen (§36), and being a raw `Tooltip` it gets none of
 * `InfoTooltip`'s behaviour: no footer, no deep link to the definition, no in-place
 * overlay, and above all no glossary. Its body is a hand-typed sentence, which is the
 * drift §44 exists to prevent.
 *
 * ## It takes a term id, not a string, and that is the point
 *
 * The signature deliberately offers no way to pass prose. A column header explaining
 * itself is explaining a TERM OF ART — that is the whole reason it needs explaining — so
 * the definition belongs in `docs/glossary.json` where the glossary page, search and every
 * other surface read the same sentence. If a header genuinely needs to explain a CONTROL
 * rather than a term, use `InfoTooltip` with `text` directly and do not reach for this.
 *
 * ## ⚠️ Adding this to a column WIDENS its minimum width
 *
 * The glyph is 14px with a 4px gap, so a header costs **18px more** than its text alone
 * (§49: a column header is never truncated, and a number column is sized by its HEADER,
 * not its values). Several inventory columns were already measured to the pixel against
 * their bare text — `Threshold` sits at 140 precisely because 120 truncated it to
 * "Thresh…" — so every column that gains a term here needs its `width` re-checked in the
 * browser, not estimated from character count.
 *
 * Usage:
 *
 *     { field: 'capacity', headerName: 'Capacity', width: 150,
 *       renderHeader: headerWithTerm('Capacity', 'inventory-capacity') }
 *
 * `headerName` is still passed because the grid uses it for the column menu, the
 * column-visibility panel and CSV export, none of which render `renderHeader`.
 */
export const headerWithTerm =
  (label: string, termId: string) =>
  (): React.ReactElement => (
    <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
      {/*
        The grid's own header cell applies the column's font and overflow rules to this
        subtree, so the label is left as plain text rather than wrapped in a `Typography`
        that would re-declare them and drift from the untooltipped columns beside it.
      */}
      <span>{label}</span>
      {/*
        `InfoTooltip` supplies its own 4px left margin on the default glyph, which is why
        the Stack carries no `spacing`. Adding one would double the gap and make a
        tooltipped header sit differently from the measurements above.
      */}
      <InfoTooltip term={termId} />
    </Stack>
  )
